import { useMemo, useState, type ReactNode } from 'react';
import {
  UMAccordionSection, UMButton, UMCheckbox, UMFormGroup, UMFormStyles, UMIcon, UMMap, UMMessage, UMMultiSelect, UMR, UMSegmentedControl, UMSelect,
} from '../../urbanmap/_components';
import { DEFAULT_HYPOTHESES } from './mocks';
import { planLayers, planSources, DEFAULT_VISIBILITY } from './mapLayers';
import { PLAN, STATUS_LABEL } from './tokens';
import type { Hypotheses, PlanBootstrap, PlanProject, Scenario, ScenarioStatus } from './types';
import { NumberField, Pill, fmt } from './ui';

const USAGES = ['Habitat collectif', 'Habitat individuel', 'Tertiaire', 'Enseignement', 'Santé', 'Commerce', 'Industrie'];

const STATUS_TONE: Record<ScenarioStatus, 'neutral' | 'accent' | 'success' | 'warning'> = {
  draft: 'neutral', running: 'accent', done: 'success', stale: 'warning',
};

interface Props {
  data: PlanBootstrap;
  project: PlanProject;
  modifiedCount: number;
  scenarios: Scenario[];
  selectedId: string;
  onSelect: (id: string) => void;
  onUpdate: (id: string, patch: Partial<Hypotheses>) => void;
  onRun: (id: string) => void;
  onDuplicate: (id: string) => void;
  onCreate: () => void;
  onRename: (id: string, name: string) => void;
  onShowOnMap: (id: string) => void;
  onGoToMap: () => void;
}

export function StudyView(p: Props) {
  const [compare, setCompare] = useState<string[]>([]);
  const [comparing, setComparing] = useState(false);
  const [tab, setTab] = useState<'Hypothèses' | 'Résultats'>('Hypothèses');
  const scenario = p.scenarios.find((s) => s.id === p.selectedId) ?? p.scenarios[0];
  const insideCount = useMemo(() => p.data.geo.buildings.features.filter((f) => f.properties?.inside === 1).length, [p.data.geo.buildings]);

  const toggleCompare = (id: string) => setCompare((c) => (c.includes(id) ? c.filter((x) => x !== id) : c.length >= 3 ? c : [...c, id]));

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0, fontFamily: UMR.font, color: UMR.black, background: 'var(--nj-core-color-grey-100, #f6f8f9)' }}>
      <UMFormStyles />
      {/* Scenario list */}
      <aside style={{ width: 300, minWidth: 300, background: UMR.white, borderRight: `1px solid ${UMR.border}`, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700, fontSize: 18 }}>Scénarios</span>
          <span style={{ fontSize: 12, color: UMR.textSecondary }}>{p.scenarios.length}</span>
        </div>
        <div style={{ padding: '0 16px 12px', fontSize: 12, color: UMR.textSecondary }}>Chaque scénario porte ses propres hypothèses.</div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {p.scenarios.map((s) => {
            const active = !comparing && s.id === scenario?.id;
            return (
              <div
                key={s.id}
                role="button"
                tabIndex={0}
                onClick={() => { p.onSelect(s.id); setComparing(false); }}
                onKeyDown={(e) => e.key === 'Enter' && p.onSelect(s.id)}
                style={{
                  padding: '12px 16px', cursor: 'pointer', borderLeft: `3px solid ${active ? PLAN.accent : 'transparent'}`, background: active ? PLAN.accentBg : 'transparent',
                  borderBottom: `1px solid ${UMR.borderSubtle}`, display: 'flex', gap: 8,
                }}
              >
                <div onClick={(e) => e.stopPropagation()} title="Comparer">
                  <UMCheckbox checked={compare.includes(s.id)} onChange={() => toggleCompare(s.id)} label="" />
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Pill tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</Pill>
                    <span style={{ fontSize: 11, color: UMR.textSecondary }}>{s.updatedAt}</span>
                  </div>
                  {s.status === 'running' && (
                    <div style={{ height: 4, background: UMR.borderSubtle }}>
                      <div style={{ height: 4, width: `${s.progress}%`, background: PLAN.accent, transition: 'width .2s' }} />
                    </div>
                  )}
                  {s.results && s.status !== 'running' && (
                    <div style={{ fontSize: 12, color: UMR.textSecondary }}>
                      {s.results.connectedBuildings.length} bât. · {fmt(s.results.networkKm, 1)} km · {fmt(s.results.renewableRate)} % EnR&R
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8, borderTop: `1px solid ${UMR.border}` }}>
          <UMButton variant="secondary" icon="add" fullWidth onClick={p.onCreate}>Nouveau scénario</UMButton>
          <UMButton icon="compare_arrows" fullWidth disabled={compare.length < 2} onClick={() => setComparing(true)}>
            Comparer ({compare.length}/3)
          </UMButton>
        </div>
      </aside>

      <section style={{ flex: 1, minWidth: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Input data strip: makes the base explicit */}
        <div style={{ background: PLAN.accentBg, borderBottom: `1px solid ${PLAN.accentBorder}`, padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <UMIcon name="database" size={18} color={PLAN.accentStrong} />
          <span>
            Données d’entrée : <strong>copie du projet du {p.project.snapshotDate}</strong> · {fmt(insideCount)} bâtiments · {p.modifiedCount} modifiés dans le projet · {p.project.perimeterLabel}
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={p.onGoToMap} style={{ border: 'none', background: 'transparent', color: UMR.blueLink, cursor: 'pointer', fontSize: 13 }}>Voir les données sur la carte</button>
        </div>

        {comparing ? (
          <Comparison scenarios={p.scenarios.filter((s) => compare.includes(s.id))} onClose={() => setComparing(false)} onShowOnMap={p.onShowOnMap} />
        ) : scenario ? (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1100 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                aria-label="Nom du scénario"
                value={scenario.name}
                onChange={(e) => p.onRename(scenario.id, e.target.value)}
                style={{ flex: 1, fontSize: 22, fontWeight: 700, border: '1px solid transparent', background: 'transparent', padding: '4px 6px', fontFamily: UMR.font }}
              />
              <Pill tone={STATUS_TONE[scenario.status]}>{STATUS_LABEL[scenario.status]}</Pill>
              <UMButton variant="secondary" icon="content_copy" onClick={() => p.onDuplicate(scenario.id)}>Dupliquer</UMButton>
              <UMButton variant="secondary" icon="map" disabled={!scenario.results} onClick={() => p.onShowOnMap(scenario.id)}>Voir sur la carte</UMButton>
              <UMButton icon="play_arrow" disabled={scenario.status === 'running'} onClick={() => { p.onRun(scenario.id); setTab('Résultats'); }}>
                {scenario.status === 'running' ? `Calcul… ${scenario.progress} %` : scenario.results ? 'Relancer le calcul' : 'Lancer le calcul'}
              </UMButton>
            </div>
            <div style={{ width: 320 }}>
              <UMSegmentedControl options={['Hypothèses', 'Résultats']} value={tab} onChange={(v) => setTab(v as typeof tab)} />
            </div>
            {tab === 'Hypothèses'
              ? <HypothesesForm s={scenario} onChange={(patch) => p.onUpdate(scenario.id, patch)} />
              : <Results data={p.data} s={scenario} onRun={() => p.onRun(scenario.id)} />}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div style={{ background: UMR.white, border: `1px solid ${UMR.border}`, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {title && <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>}
      {children}
    </div>
  );
}

function HypothesesForm({ s, onChange }: { s: Scenario; onChange: (patch: Partial<Hypotheses>) => void }) {
  const h = s.hypotheses;
  const mix = h.biomass + h.geothermal + h.recovery;
  const changed = (k: keyof Hypotheses) => JSON.stringify(h[k]) !== JSON.stringify(DEFAULT_HYPOTHESES[k]);
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 } as const;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <UMMessage title="Hypothèses propres à ce scénario">
        Modifier une valeur n’affecte que « {s.name} ». Pour tester une variante, dupliquez le scénario.
        {s.status === 'stale' && <strong> Les résultats affichés ne correspondent plus : relancez le calcul.</strong>}
      </UMMessage>
      <Card>
        <UMAccordionSection title="Demande & raccordement" defaultOpen>
          <div style={grid}>
            <NumberField label={`Demande minimale par bâtiment${changed('minDemand') ? ' •' : ''}`} value={h.minDemand} unit="MWh/an" onChange={(v) => onChange({ minDemand: v })} />
            <NumberField label="Taux de raccordement visé" value={h.connectionRate} unit="%" onChange={(v) => onChange({ connectionRate: Math.min(100, v) })} />
            <div style={{ gridColumn: '1 / -1' }}>
              <UMFormGroup label="Usages éligibles">
                <UMMultiSelect options={USAGES} value={h.usages} onChange={(v) => onChange({ usages: v })} />
              </UMFormGroup>
            </div>
          </div>
        </UMAccordionSection>
        <UMAccordionSection title="Réseau" defaultOpen>
          <div style={grid}>
            <UMFormGroup label="Niveau de température">
              <UMSelect ariaLabel="Température" value={h.temperature} options={['Basse', 'Moyenne', 'Haute'].map((v) => ({ value: v, label: v }))} onChange={(v) => onChange({ temperature: v as Hypotheses['temperature'] })} />
            </UMFormGroup>
            <NumberField label="Densité linéique minimale" value={h.minDensity} unit="MWh/ml/an" onChange={(v) => onChange({ minDensity: v })} />
            <UMFormGroup label="Horizon du plan">
              <UMSelect ariaLabel="Horizon" value={h.horizon} options={['2030', '2035', '2040'].map((v) => ({ value: v, label: v }))} onChange={(v) => onChange({ horizon: v as Hypotheses['horizon'] })} />
            </UMFormGroup>
          </div>
        </UMAccordionSection>
        <UMAccordionSection title="Mix EnR&R">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
            <NumberField label="Biomasse" value={h.biomass} unit="%" onChange={(v) => onChange({ biomass: v })} />
            <NumberField label="Géothermie" value={h.geothermal} unit="%" onChange={(v) => onChange({ geothermal: v })} />
            <NumberField label="Chaleur fatale" value={h.recovery} unit="%" onChange={(v) => onChange({ recovery: v })} />
          </div>
          <div style={{ fontSize: 13, marginTop: 8, color: mix > 100 ? 'var(--nj-core-color-red-600, #db3735)' : UMR.textSecondary }}>
            Part EnR&R totale : <strong>{mix} %</strong> · appoint gaz : {Math.max(0, 100 - mix)} %{mix > 100 && ' — la somme dépasse 100 %'}
          </div>
        </UMAccordionSection>
        <UMAccordionSection title="Économie">
          <div style={grid}>
            <NumberField label="Prix de vente de la chaleur" value={h.heatPrice} unit="€/MWh" onChange={(v) => onChange({ heatPrice: v })} />
            <NumberField label="Coût de pose réseau" value={h.capexPerMeter} unit="€/ml" onChange={(v) => onChange({ capexPerMeter: v })} />
            <NumberField label="Taux de subvention (Fonds Chaleur)" value={h.subsidyRate} unit="%" onChange={(v) => onChange({ subsidyRate: v })} />
            <NumberField label="Taux d’actualisation" value={h.discountRate} unit="%" onChange={(v) => onChange({ discountRate: v })} />
          </div>
        </UMAccordionSection>
      </Card>
    </div>
  );
}

function Kpi({ label, value, unit, best }: { label: string; value: string; unit: string; best?: boolean }) {
  return (
    <div style={{ background: UMR.white, border: `1px solid ${best ? PLAN.accent : UMR.border}`, padding: '12px 16px', minWidth: 0 }}>
      <div style={{ fontSize: 12, color: UMR.textSecondary }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700 }}>{value} <span style={{ fontSize: 13, fontWeight: 400, color: UMR.textSecondary }}>{unit}</span></div>
    </div>
  );
}

function Results({ data, s, onRun }: { data: PlanBootstrap; s: Scenario; onRun: () => void }) {
  const connected = useMemo(() => new Set(s.results?.connectedBuildings ?? []), [s.results]);
  const sources = useMemo(() => planSources(data.geo, { modified: {}, drift: new Set(), connected }), [data.geo, connected]);
  const layers = useMemo(() => planLayers({ ...DEFAULT_VISIBILITY, zone: false }, { pickedId: null, scenarioShown: true }), []);

  if (s.status === 'running') {
    return (
      <Card>
        <div style={{ fontSize: 14 }}>Calcul du plan de développement sur la copie du projet… {s.progress} %</div>
        <div style={{ height: 6, background: UMR.borderSubtle }}><div style={{ height: 6, width: `${s.progress}%`, background: PLAN.accent, transition: 'width .2s' }} /></div>
      </Card>
    );
  }
  if (!s.results) {
    return (
      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 32, textAlign: 'center' }}>
          <UMIcon name="insights" size={40} color={UMR.textDisabled} />
          <div style={{ fontWeight: 700 }}>Pas encore de résultats</div>
          <div style={{ fontSize: 13, color: UMR.textSecondary, maxWidth: 420 }}>Vérifiez les hypothèses de ce scénario puis lancez le calcul. Il s’exécute sur les données du projet, pas sur UrbanMap.</div>
          <UMButton icon="play_arrow" onClick={onRun}>Lancer le calcul</UMButton>
        </div>
      </Card>
    );
  }
  const r = s.results;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {s.status === 'stale' && (
        <div style={{ background: PLAN.warningBg, border: `1px solid ${PLAN.warningBorder}`, padding: '8px 12px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          <UMIcon name="warning" size={18} color={PLAN.warningText} />
          Les hypothèses ont changé depuis le dernier calcul — ces résultats sont obsolètes.
          <span style={{ flex: 1 }} />
          <UMButton variant="secondary" icon="refresh" onClick={onRun}>Relancer</UMButton>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 8, opacity: s.status === 'stale' ? 0.6 : 1 }}>
        <Kpi label="Bâtiments raccordés" value={fmt(r.connectedBuildings.length)} unit="" />
        <Kpi label="Linéaire réseau" value={fmt(r.networkKm, 1)} unit="km" />
        <Kpi label="Chaleur livrée" value={fmt(r.deliveredGWh, 1)} unit="GWh/an" />
        <Kpi label="Taux EnR&R" value={fmt(r.renewableRate)} unit="%" />
        <Kpi label="CAPEX" value={fmt(r.capexMEur, 1)} unit="M€" />
        <Kpi label="TRI projet" value={fmt(r.irr, 1)} unit="%" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
        <Card title="Phasage du plan de développement">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: UMR.textSecondary }}>
                {['Phase', 'Bâtiments', 'Linéaire (km)', 'CAPEX (M€)'].map((h) => <th key={h} style={{ padding: '6px 4px', borderBottom: `1px solid ${UMR.border}`, fontWeight: 600 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {r.phases.map((ph) => (
                <tr key={ph.year}>
                  <td style={{ padding: '6px 4px', borderBottom: `1px solid ${UMR.borderSubtle}` }}>{ph.year}</td>
                  <td style={{ padding: '6px 4px', borderBottom: `1px solid ${UMR.borderSubtle}` }}>{ph.buildings}</td>
                  <td style={{ padding: '6px 4px', borderBottom: `1px solid ${UMR.borderSubtle}` }}>{fmt(ph.km, 1)}</td>
                  <td style={{ padding: '6px 4px', borderBottom: `1px solid ${UMR.borderSubtle}` }}>{fmt(ph.capexMEur, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 12, color: UMR.textSecondary }}>Densité linéique moyenne : {fmt(r.density, 2)} MWh/ml/an</div>
        </Card>
        <Card title="Bâtiments raccordés">
          <div style={{ position: 'relative', height: 260 }}>
            <UMMap sources={sources} layers={layers} initialBounds={data.geo.bounds} />
          </div>
        </Card>
      </div>
    </div>
  );
}

const HYP_ROWS: { key: keyof Hypotheses; label: string; unit?: string }[] = [
  { key: 'minDemand', label: 'Demande minimale', unit: 'MWh/an' },
  { key: 'connectionRate', label: 'Taux de raccordement', unit: '%' },
  { key: 'temperature', label: 'Température' },
  { key: 'biomass', label: 'Biomasse', unit: '%' },
  { key: 'geothermal', label: 'Géothermie', unit: '%' },
  { key: 'subsidyRate', label: 'Subvention', unit: '%' },
  { key: 'horizon', label: 'Horizon' },
];

function Comparison({ scenarios, onClose, onShowOnMap }: { scenarios: Scenario[]; onClose: () => void; onShowOnMap: (id: string) => void }) {
  const kpis: { label: string; unit: string; get: (s: Scenario) => number | undefined; higher: boolean; digits?: number }[] = [
    { label: 'Bâtiments raccordés', unit: '', get: (s) => s.results?.connectedBuildings.length, higher: true },
    { label: 'Linéaire réseau', unit: 'km', get: (s) => s.results?.networkKm, higher: false, digits: 1 },
    { label: 'Chaleur livrée', unit: 'GWh/an', get: (s) => s.results?.deliveredGWh, higher: true, digits: 1 },
    { label: 'Taux EnR&R', unit: '%', get: (s) => s.results?.renewableRate, higher: true },
    { label: 'CAPEX', unit: 'M€', get: (s) => s.results?.capexMEur, higher: false, digits: 1 },
    { label: 'TRI projet', unit: '%', get: (s) => s.results?.irr, higher: true, digits: 1 },
  ];
  const cell = { padding: '8px 12px', borderBottom: `1px solid ${UMR.borderSubtle}`, fontSize: 13 } as const;
  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 22, fontWeight: 700, flex: 1 }}>Comparaison de {scenarios.length} scénarios</span>
        <UMButton variant="secondary" icon="close" onClick={onClose}>Fermer</UMButton>
      </div>
      <Card>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...cell, textAlign: 'left' }} />
              {scenarios.map((s) => (
                <th key={s.id} style={{ ...cell, textAlign: 'left' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span>{s.name}</span>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <Pill tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</Pill>
                      {s.results && <button type="button" onClick={() => onShowOnMap(s.id)} style={{ border: 'none', background: 'transparent', color: UMR.blueLink, cursor: 'pointer', fontSize: 12, padding: 0 }}>Carte</button>}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr><td colSpan={scenarios.length + 1} style={{ ...cell, fontWeight: 700, background: 'var(--nj-core-color-grey-100, #f6f8f9)' }}>Résultats</td></tr>
            {kpis.map((k) => {
              const vals = scenarios.map(k.get).filter((v): v is number => v !== undefined);
              const best = vals.length ? (k.higher ? Math.max(...vals) : Math.min(...vals)) : undefined;
              return (
                <tr key={k.label}>
                  <td style={{ ...cell, color: UMR.textSecondary }}>{k.label}{k.unit && ` (${k.unit})`}</td>
                  {scenarios.map((s) => {
                    const v = k.get(s);
                    const isBest = v !== undefined && v === best && vals.length > 1;
                    return <td key={s.id} style={{ ...cell, fontWeight: isBest ? 700 : 400, color: isBest ? PLAN.accentStrong : UMR.black }}>{v === undefined ? '—' : fmt(v, k.digits ?? 0)}{isBest && ' ★'}</td>;
                  })}
                </tr>
              );
            })}
            <tr><td colSpan={scenarios.length + 1} style={{ ...cell, fontWeight: 700, background: 'var(--nj-core-color-grey-100, #f6f8f9)' }}>Hypothèses (différences surlignées)</td></tr>
            {HYP_ROWS.map((row) => {
              const vals = scenarios.map((s) => String(s.hypotheses[row.key]));
              const differs = new Set(vals).size > 1;
              return (
                <tr key={row.key}>
                  <td style={{ ...cell, color: UMR.textSecondary }}>{row.label}{row.unit && ` (${row.unit})`}</td>
                  {vals.map((v, i) => <td key={scenarios[i].id} style={{ ...cell, background: differs ? PLAN.warningBg : 'transparent' }}>{v}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
